// MOD-THERMOS (E12-T14, ux/08 §8): «+50 энергии за {m} талонов. Сегодня осталось {k} из 5», цена следующего
// термоса с сервера, предупреждение о сгорании сверх предела до оплаты, нехватка талонов — как в §2.1.
// Цена на кнопке — та, что прислал сервер (GET /shop или ответ прошлого термоса), клиент её не считает.
import { useRef, useState } from 'preact/hooks';
import { useApp } from '../app/app.js';
import { num } from '../i18n/format.js';
import { t as tr } from '../i18n/t.js';
import { ApiCallError } from '../net/api-client.js';
import { baseRate } from '../pay/flow.js';
import { clientConfig, online, setWallet, shop, wallet } from '../state/game.js';
import type { SheetProps } from '../ui/App.js';
import { Sheet } from '../ui/kit.js';
import { showToast } from '../ui/toast.js';
import { I } from '../assets/icons.js';
import { coverPack, inDebt, talonsHint, tn } from './store-model.js';
import { bumpShop, starPacks, thermosLocal, useExtras, useThermos } from './store-goods.js';
import { HoldButton, TalonPrice, StarsIcon } from './store-ui.js';

/**
 * Прибавка и предел энергии для предупреждения о сгорании: из конфига (`economy.energy`), иначе — запасные
 * значения для показа (канон §10: +50, предел 200). Сервер всё равно режет по своему конфигу.
 */
export function thermosNumbers(cfg: Record<string, unknown>): { add: number; cap: number } {
  const e = (cfg['economy.energy'] ?? (cfg['economy'] as Record<string, unknown> | undefined)?.['energy']) as { thermosEnergy?: number; hardCap?: number } | undefined;
  return { add: e?.thermosEnergy ?? 50, cap: e?.hardCap ?? 200 };
}

/** Сколько энергии сгорит: прибавка сверх предела. */
export function thermosBurn(value: number, add: number, cap: number): number {
  return Math.max(0, value + add - cap);
}

export function ThermosSheet({ entry }: SheetProps) {
  const app = useApp();
  const th = useThermos();
  const w = wallet.value;
  const packs = starPacks(shop.value?.data ?? null);
  const rate = baseRate(packs);
  const ex = useExtras(shop.value?.data ?? null);
  const { add, cap } = thermosNumbers(clientConfig.value);
  const key = useRef(app.idem());
  const [drunk, setDrunk] = useState(false);
  const price = th?.nextPriceTalons ?? null;
  const left = th?.leftToday ?? 0;
  const burn = thermosBurn(w?.energy.value ?? 0, add, cap);
  const debt = inDebt(w);
  const lack = price !== null && (w?.talons ?? 0) < price ? price - Math.max(0, w?.talons ?? 0) : 0;
  const cover = lack ? coverPack(lack, packs.filter((x) => !x.blocked)) : null;
  const drink = async () => {
    try {
      const r = await app.call('buyThermos', { body: {}, idempotencyKey: key.current });
      key.current = app.idem();
      setWallet(r.wallet);
      thermosLocal.value = { nextPriceTalons: r.nextPriceTalons, leftToday: r.thermosLeftToday, perDay: th?.perDay ?? 5, at: Date.now() };
      bumpShop();
      app.haptic('success');
      setDrunk(true);
      setTimeout(() => app.nav.popSheet(), 1100);
    } catch (e) {
      const code = e instanceof ApiCallError ? e.code : 'INTERNAL';
      if (code !== 'NETWORK' && code !== 'TIMEOUT') key.current = app.idem();
      app.haptic('error');
      if (code === 'DAILY_LIMIT') { thermosLocal.value = { nextPriceTalons: null, leftToday: 0, perDay: th?.perDay ?? 5, at: Date.now() }; showToast(tr('SHOP-THERMOS-LIMIT'), 'error'); return; }
      if (code === 'TALONS_DEBT') { showToast(tr('SHOP-DEBT'), 'error'); return; }
      if (code === 'NOT_ENOUGH_TALONS') { void app.call('getWallet').then(setWallet).catch(() => {}); showToast(tr('CL-STORE-ERR-TALONS'), 'error'); return; }
      if (code === 'INTERNAL' || code === 'NETWORK' || code === 'TIMEOUT') { showToast(tr('CL-STORE-ERR-SAFE'), 'error'); return; }
      app.handleError(e, { resource: tr('CL-STORE-TALONS-SAFE') });
    }
  };
  // Шестой за сутки: окно не открывается — строка источника говорит «Снова завтра» (E12-T14).
  const done = !!th && left <= 0;
  let cta;
  if (!online.value) cta = <HoldButton text={tr('UI-OFFLINE')} hold={false} disabled testId="thermos-buy" onDone={() => {}} />;
  else if (done) cta = <HoldButton text={tr('CL-STORE-AGAIN-TOMORROW')} hold={false} disabled testId="thermos-buy" onDone={() => {}} />;
  else if (debt) cta = <HoldButton text={tr('CL-STORE-DEBT-PRICE')} hold={false} disabled testId="thermos-buy" onDone={() => {}} />;
  else if (price === null) cta = <HoldButton text={tr('CL-STORE-SOON')} hold={false} disabled testId="thermos-buy" onDone={() => {}} />;
  else if (lack) {
    cta = <HoldButton text={tr('CL-STORE-LACK', tn(lack))} hold={false} disabled={!cover} testId="thermos-buy"
      onDone={() => { if (cover) app.nav.openSheet('MOD-PURCHASE', { product: cover.pack, rate, returnTo: { id: 'MOD-THERMOS', params: entry.params } }); }} />;
  } else cta = <HoldButton text={burn ? tr('CL-THERMOS-DRINK-ANYWAY', tn(price)) : tr('CL-THERMOS-DRINK', tn(price))} hold={false} testId="thermos-buy" onDone={drink} />;
  return (
    <Sheet entry={entry} title={tr('CL-STORE-G-THERMOS')}>
      <div class="col" style={{ alignItems: 'center', textAlign: 'center' }} data-testid="MOD-THERMOS-body">
        <span class={`thermos-pic ${drunk ? 'drunk' : ''}`}><I n="energy" size={64} /></span>
        {drunk ? <b class="pos" data-testid="thermos-done">{tr('CL-THERMOS-PLUS', { n: add })}</b> : (
          <>
            <p style={{ margin: 0 }} data-testid="thermos-text">
              {price !== null ? tr('CL-THERMOS-TEXT', { n: add, m: num(price), k: left, t: th?.perDay ?? 5 }) : tr('CL-THERMOS-TEXT-NOPRICE', { n: add })}
            </p>
            {price !== null ? <TalonPrice n={price} hint={talonsHint(price, rate, ex.rubPerStar)} /> : null}
            <span class="small muted">{tr('CL-STORE-HAVE', tn(w?.talons ?? 0))} · {tr('CL-THERMOS-ENERGY', { v: w?.energy.value ?? 0 })}</span>
            {burn > 0 && !done ? <div class="banner warn" data-testid="thermos-burn">{tr('CL-THERMOS-BURN', { cap, n: burn })}</div> : null}
            {debt ? <div class="banner warn">{tr('CL-STORE-DEBT-BANNER', tn(w?.talonsDebt ?? Math.abs(Math.min(0, w?.talons ?? 0))))}</div> : null}
            {cover ? <span class="small" data-testid="lack-stars">{tr('CL-STORE-LACK-STARS', { n: num(cover.stars) })}<StarsIcon size={14} /></span> : null}
            <div class="purchase-cta">{cta}</div>
            <span class="small muted">{tr('CL-THERMOS-RESET')}</span>
          </>
        )}
      </div>
    </Sheet>
  );
}

/** Строка термоса в MOD-ENERGY: цена следующего и остаток; 6-й за сутки — «Снова завтра», окно не открывается. */
export function ThermosRow() {
  const app = useApp();
  const th = useThermos();
  const done = !!th && th.leftToday <= 0;
  return (
    <button class="list-row" data-testid="energy-thermos" disabled={done || !online.value} onClick={() => app.nav.openSheet('MOD-THERMOS', {}, { replace: true })}>
      <I n="energy" size={22} />
      <span style={{ flex: 1 }}>{tr('UI-ENERGY-THERMOS')}</span>
      <span class="small muted">{done ? tr('CL-STORE-AGAIN-TOMORROW') : th?.nextPriceTalons ? tr('CL-THERMOS-ROW', { m: num(th.nextPriceTalons), k: th.leftToday, n: th.perDay }) : '›'}</span>
    </button>
  );
}
